import { Box, Button, Theme } from '@material-ui/core';
import { Archive, AssignmentReturn, ChatBubble, Email, ExpandMore, Flag, Restore } from '@material-ui/icons';
import { TabletButton } from '@campfire/tablet-button';
import { createStyles, makeStyles } from '@material-ui/styles';
import Icon from '@mdi/react';
import { mdiChat } from '@mdi/js';
import React, { Fragment, useRef, useState } from 'react';
import PrivilegeRules from '../../../../../global/auth/PrivilegeRules';
import { useUser } from '../../../../../global/auth/useUser';
import { useCampfireTheme } from '../../../../../theme/useCampfireTheme';
import { TooltipMenu } from '../../../../../common/tooltip-menu';
import { useVolunteerCommonProfileContext } from '../../VolunteerCommonProfileContext';
import { VolunteerCommonProfile } from '../../__generated__/VolunteerCommonProfile';
import { VolunteerCommonProfileExitDialog } from './VolunteerCommonProfileExitDialog';
import { VolunteerCommonProfileReactivateDialog } from './VolunteerCommonProfileReactivateDialog';
import { VolunteerCommonProfileRetireDialog } from './VolunteerCommonProfileRetireDialog';
import { VolunteerCommonProfileReturnToApplicantDialog } from './VolunteerCommonProfileReturnToApplicantDialog';
import { VolunteerCommonProfileEmailDialog } from './VolunteerCommonProfileEmailDialog';
import { VolunteerCommonProfileSmsDialog } from './VolunteerCommonProfileSmsDialog';
import { useOrgInfo } from '../../../../../global/auth/use-org-info/use-org-info';

export type VolunteerCommonProfileActionMenuOption = 'retire' | 'flagAndExit' | 'reactivate' | 'returnToApplicant';

const actionMenuOptionLabels = {
  retire: 'Retire',
  flagAndExit: 'Flag and Exit',
  reactivate: 'Reactivate',
  returnToApplicant: 'Return to Applicant',
};

const actionMenuOptionIcons = {
  retire: Archive,
  flagAndExit: Flag,
  reactivate: Restore,
  returnToApplicant: AssignmentReturn,
};

const getActionMenuOptions = (volunteer: VolunteerCommonProfile): Array<VolunteerCommonProfileActionMenuOption> => {
  if (volunteer.rosterStatus === 'flagged') {
    return [];
  }

  const actions: Array<VolunteerCommonProfileActionMenuOption> = [];
  const {
    user: { userIdentityService },
  } = useUser();

  const canFlag =
    userIdentityService.hasVmGlobalRule(PrivilegeRules.flagVolunteer) ||
    !!volunteer.programs.find((program) =>
      userIdentityService.hasRuleForProgram(program.programId, PrivilegeRules.flagVolunteer)
    );
  if (canFlag) actions.push('flagAndExit');

  const canRetire =
    userIdentityService.hasVmGlobalRule(PrivilegeRules.retireVolunteer) ||
    !!volunteer.programs.find((program) =>
      userIdentityService.hasRuleForProgram(program.programId, PrivilegeRules.retireVolunteer)
    );

  if (!volunteer.dateDeactivated && canRetire) {
    actions.push('retire');
  }

  if (canRetire) {
    actions.push('returnToApplicant');
  }

  if (volunteer.dateDeactivated) {
    const canUnretire =
      userIdentityService.hasVmGlobalRule(PrivilegeRules.unretireVolunteer) ||
      !!volunteer.programs.find((program) =>
        userIdentityService.hasRuleForProgram(program.programId, PrivilegeRules.unretireVolunteer)
      );
    if (canUnretire) actions.push('reactivate');
  }

  return actions;
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    hideRightSeparator: {
      '.MuiDataGrid-columnSeparator': {
        display: 'none !important',
      },
    },
    edit: {
      color: theme.alert.blue.extraLight,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '14px',
      fontWeight: 500,
      '& span': {
        fontSize: '14px',
      },
      '& p': {
        fontSize: '14px',
      },
      '& svg': {
        fontSize: '16px',
      },
      textTransform: 'none',
    },
  })
);

export const VolunteerCommonProfileActionMenu = ({
  volunteer,
  reloadDialog,
}: {
  volunteer: VolunteerCommonProfile;
  reloadDialog?: () => void;
}) => {
  const classes = useStyles();
  const [openActionMenu, setOpenActionMenu] = useState(false);
  const handleActionMenuOpen = () => setOpenActionMenu(true);
  const handleActionMenuClose = () => setOpenActionMenu(false);
  const actionMenuAnchorRef = useRef<HTMLButtonElement>(null);
  const [retireDialogOpen, setRetireDialogOpen] = useState(false);
  const [exitDialogOpen, setExitDialogOpen] = useState(false);
  const [reactivateDialogOpen, setReactivateDialogOpen] = useState(false);
  const [returnToApplicantDialogOpen, setReturnToApplicantDialogOpen] = useState(false);
  const [communicateMenuOpen, setCommunicateMenuOpen] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [smsDialogOpen, setSmsDialogOpen] = useState(false);
  const volunteerFullName = `${volunteer.profile.firstName} ${volunteer.profile.lastName}`;
  const orgInfo = useOrgInfo();

  const actionMenuOptions = volunteer.profile.email !== 'support@volaby.org' ? getActionMenuOptions(volunteer) : [];

  const handleActionMenuItemClick = (option: VolunteerCommonProfileActionMenuOption) => {
    handleActionMenuClose();
    switch (option) {
      case 'retire':
        setRetireDialogOpen(true);
        break;
      case 'flagAndExit':
        setExitDialogOpen(true);
        break;
      case 'reactivate':
        setReactivateDialogOpen(true);
        break;
      case 'returnToApplicant':
        setReturnToApplicantDialogOpen(true);
        break;
      default:
        console.error('Unknown volunteer management action');
        break;
    }
  };

  const { theme } = useCampfireTheme();
  const { updateParentVolunteerActions } = useVolunteerCommonProfileContext();

  const handleExitSuccess = () => {
    if (updateParentVolunteerActions) {
      const { updateVolunteerAsExited } = updateParentVolunteerActions;
      if (updateVolunteerAsExited) updateVolunteerAsExited();
    }
    setExitDialogOpen(false);
    if (reloadDialog) reloadDialog();
  };
  const handleRetireSuccess = () => {
    if (updateParentVolunteerActions) {
      const { updateVolunteerAsRetired } = updateParentVolunteerActions;
      if (updateVolunteerAsRetired) updateVolunteerAsRetired();
    }
    setRetireDialogOpen(false);
    if (reloadDialog) reloadDialog();
  };
  const handleReactivateSuccess = () => {
    if (updateParentVolunteerActions) {
      const { updateVolunteerAsReactivated } = updateParentVolunteerActions;
      if (updateVolunteerAsReactivated) updateVolunteerAsReactivated();
    }
    setReactivateDialogOpen(false);
    if (reloadDialog) reloadDialog();
  };
  const handleReturnToApplicantSuccess = () => {
    if (updateParentVolunteerActions) {
      const { updateVolunteerAsRetired } = updateParentVolunteerActions;
      if (updateVolunteerAsRetired) updateVolunteerAsRetired();
    }
    setReturnToApplicantDialogOpen(false);
    if (reloadDialog) reloadDialog();
  };
  return (
    <Fragment>
      <VolunteerCommonProfileEmailDialog
        volunteers={volunteer}
        open={emailDialogOpen}
        onClose={() => setEmailDialogOpen(false)}
      />
      <VolunteerCommonProfileSmsDialog
        volunteers={volunteer}
        open={smsDialogOpen}
        onClose={() => setSmsDialogOpen(false)}
      />
      <TooltipMenu
        open={communicateMenuOpen}
        onClose={() => {
          setCommunicateMenuOpen(false);
        }}
        title={
          <Box
            display='flex'
            flexDirection='column'
            alignItems='flex-start'
            style={{ padding: '5px 10px', justifyContent: 'flex-start' }}
          >
            <Button
              className={classes.edit}
              startIcon={<Email />}
              onClick={() => {
                setCommunicateMenuOpen(false);
                setEmailDialogOpen(true);
              }}
            >
              Email
            </Button>
            {orgInfo?.smsEnabled && (
              <Button
                className={classes.edit}
                startIcon={<ChatBubble />}
                onClick={() => {
                  setCommunicateMenuOpen(false);
                  setSmsDialogOpen(true);
                }}
              >
                SMS
              </Button>
            )}
          </Box>
        }
      >
        <TabletButton
          data-track='fs-volPrf-menu-action-menu-button'
          size='small'
          variant='contained'
          color='primary'
          aria-controls='task-status-menu'
          aria-haspopup='true'
          onClick={() => setCommunicateMenuOpen(true)}
          startIcon={<Icon path={mdiChat} horizontal style={{ height: '15px' }} />}
          endIcon={<ExpandMore />}
          style={{
            height: '100%',
            border: '1px solid transparent',
            boxSizing: 'border-box',
            borderRadius: '4px',
            padding: '5px 10px',
            marginRight: '10px',
            color: '#fff',
          }}
        >
          {'Contact'}
        </TabletButton>
      </TooltipMenu>
      {actionMenuOptions.length ? (
        <Fragment>
          {!volunteer.dateDeactivated ? (
            <VolunteerCommonProfileRetireDialog
              volunteerId={volunteer.volunteerId}
              fullName={volunteerFullName}
              open={retireDialogOpen}
              onClose={() => setRetireDialogOpen(false)}
              handleSuccess={handleRetireSuccess}
            />
          ) : null}

          <VolunteerCommonProfileReturnToApplicantDialog
            volunteerId={volunteer.volunteerId}
            fullName={volunteerFullName}
            open={returnToApplicantDialogOpen}
            onClose={() => setReturnToApplicantDialogOpen(false)}
            handleSuccess={handleReturnToApplicantSuccess}
          />

          <VolunteerCommonProfileExitDialog
            volunteerId={volunteer.volunteerId}
            fullName={volunteerFullName}
            preferredName={volunteer.profile.preferredName}
            open={exitDialogOpen}
            onClose={() => setExitDialogOpen(false)}
            handleSuccess={handleExitSuccess}
          />

          {volunteer.dateDeactivated ? (
            <>
              <VolunteerCommonProfileReactivateDialog
                volunteerId={volunteer.volunteerId}
                fullName={volunteerFullName}
                preferredName={volunteer.profile.preferredName}
                open={reactivateDialogOpen}
                onClose={() => setReactivateDialogOpen(false)}
                handleSuccess={handleReactivateSuccess}
              />
            </>
          ) : null}

          <TooltipMenu
            open={openActionMenu}
            onClose={() => {
              setOpenActionMenu(false);
            }}
            title={
              <Box
                display='flex'
                flexDirection='column'
                alignItems='flex-start'
                style={{ padding: '0 5px', justifyContent: 'flex-start' }}
              >
                {actionMenuOptions.map((option) => {
                  const OptionIcon = actionMenuOptionIcons[option];
                  return (
                    <Button
                      data-track='fs-volPrf-menu-action-menu-item'
                      key={option}
                      className={classes.edit}
                      startIcon={<OptionIcon />}
                      onClick={() => handleActionMenuItemClick(option)}
                      style={option === 'flagAndExit' ? { color: '#ff5959' } : undefined}
                    >
                      {actionMenuOptionLabels[option]}
                    </Button>
                  );
                })}
              </Box>
            }
          >
            <TabletButton
              data-track='fs-volPrf-menu-action-menu-button'
              size='small'
              variant='outlined'
              color='primary'
              aria-controls='task-status-menu'
              aria-haspopup='true'
              onClick={handleActionMenuOpen}
              ref={actionMenuAnchorRef}
              endIcon={<ExpandMore />}
              style={{
                border: '1px solid #9e9e9e',
                boxSizing: 'border-box',
                borderRadius: '4px',
                padding: '5px 10px',
                color: theme.color.grey.neutral300,
                marginRight: '8px',
              }}
            >
              {'Options'}
            </TabletButton>
          </TooltipMenu>
        </Fragment>
      ) : null}
    </Fragment>
  );
};
